import React, { useState, useEffect } from "react";
import { Modal, Button } from "@/components/ui";
import { ChevronLeft, ChevronRight, Download, FileText } from "lucide-react";
import { BASE_URL } from "@/lib/API";

export const SiteVisitMediaPreviewModal = ({ isOpen, onClose, lead, mediaList = [], initialMediaIndex = 0 }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialMediaIndex);
    }
  }, [isOpen, initialMediaIndex]);

  const handleDownload = async (fileUrl, filename) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      window.open(fileUrl, '_blank');
    }
  };

  if (!lead || mediaList.length === 0) return null;

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : mediaList.length - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < mediaList.length - 1 ? prev + 1 : 0));
  };

  const currentMediaRaw = mediaList[currentIndex];
  const fileId = typeof currentMediaRaw === 'string' ? currentMediaRaw : (currentMediaRaw.fileId || currentMediaRaw._id);
  const filename = currentMediaRaw.filename || `SiteVisit_Photo_${currentIndex + 1}.jpg`;
  const mimetype = currentMediaRaw.mimetype || 'image/jpeg';
  const size = currentMediaRaw.size;
  const token = localStorage.getItem('blum_token') || localStorage.getItem('token') || '';
  const rawFileUrl = `${BASE_URL}/api/verve-leads/site-visit/media/${fileId}`;
  const fileUrl = token ? `${rawFileUrl}?token=${token}` : rawFileUrl;

  const displayName = lead.customerName || `${lead.firstName || ""} ${lead.lastName || ""}`.trim() || "Lead";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`SITE VISIT MEDIA: ${displayName.toUpperCase()}`} size="xl" closeIcon="minimize">
      <div className="flex flex-col items-center justify-center min-h-[400px] w-full bg-slate-50 rounded-xl relative p-4">
        <div className="w-full max-w-3xl h-[60vh] flex items-center justify-center bg-black/5 rounded-lg overflow-hidden relative">
          {mimetype.startsWith("video/") ? (
            <video
              src={fileUrl}
              controls
              className="max-w-full max-h-full object-contain"
              autoPlay
            />
          ) : mimetype.startsWith("application/pdf") ? (
            <iframe
              src={fileUrl}
              className="w-full h-[60vh] object-contain bg-white"
              title={filename}
            />
          ) : mimetype.startsWith("image/") ? (
            <img
              src={fileUrl}
              alt={filename}
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg w-full h-full">
              <FileText size={48} className="text-brand-blue mb-4" />
              <span className="font-semibold text-slate-800 text-lg mb-2 truncate max-w-sm" title={filename}>
                {filename}
              </span>
              <span className="text-sm text-slate-500 mb-6">Preview not available for this file type</span>
              <Button
                variant="primary"
                size="md"
                leftIcon={<Download size={16} />}
                onClick={() => handleDownload(fileUrl, filename)}
              >
                Download File
              </Button>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between w-full mt-4 px-4">
          <div className="flex flex-col">
            <span className="font-semibold text-sm text-slate-800">{filename}</span>
            <span className="text-xs text-slate-500">{size ? (size / 1024 / 1024).toFixed(2) + " MB" : "Unknown MB"}</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              size="sm"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Download size={14} />}
              onClick={() => handleDownload(fileUrl, filename)}
            >
              Download
            </Button>
          </div>
        </div>

        {mediaList.length > 1 && (
          <>
            <button
              onClick={handlePrevious}
              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all text-slate-700"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={handleNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-md transition-all text-slate-700"
            >
              <ChevronRight size={24} />
            </button>
            
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white text-xs px-3 py-1 rounded-full backdrop-blur-sm z-10">
              {currentIndex + 1} / {mediaList.length}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
